import { useState, useEffect, useMemo, useCallback } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import PageHeader from '../../components/PageHeader';
import { PackageCheck, CheckCircle2, ArrowRight, ChevronRight, ScanLine, UserRound, Hand, Eye, EyeOff } from 'lucide-react';
import Button from '../../components/ui/Button';
import BarcodeScanner from '../../components/shared/BarcodeScanner';
import { parseLotScan, scanMatches } from '../../utils/lotScan';
import { rowLabel } from '../../utils/nurseryRowSlots';
import { pickCustomers, customerDone, heldByOther, type PickCustomer } from '../../utils/pickCustomers';
import { ApiError } from '../../types/api';
import nurseryService from '../../services/nursery';
import { useOrgTimezone, formatLocalDateInTz, formatDateTimeInTz } from '../../utils/timezone';
import { errorMessage } from '../../utils/apiResponse';
import { LoadStatusBadge } from '../../components/nursery/despatch/LoadStatusBadge';
import { PickItemCard } from '../../components/nursery/picking/PickItemCard';
import { LOAD_TYPE_LABELS, type PickItem, type PickList } from '../../types/nursery';

/**
 * /nursery/picking/:id — one load's pick list, one customer at a time.
 *
 * A load carries several customers' orders. The picker first chooses a
 * customer (`?customer=`), then sees only that customer's trees — every order
 * of theirs on the load combined — in walk order, so their trees go on the
 * trolley together and the picker goes down each row once. Anything not at
 * the despatch farm sinks to the bottom, flagged. A load with one customer
 * opens straight on them.
 *
 * Several pickers can work one load, never the same customer. Opening a
 * customer claims them, and the page keeps the claim alive while it is open.
 * A customer someone else holds says who; opening them anyway shows that and
 * offers Take over, for the picker who walked off. The server enforces it:
 * a pick for a customer someone else holds is refused.
 */

/** How often an open customer's claim is renewed. The server lets one lapse after 20 minutes. */
const CLAIM_HEARTBEAT_MS = 2 * 60_000;
/** How often the customer list refreshes, so a picker sees who has taken whom. */
const CHOOSER_REFRESH_MS = 20_000;
/** Remembered per browser: whether picked-in-full cards stay on the list. */
const SHOW_PICKED_KEY = 'nursery_pick_show_picked';

/**
 * Picked in full: nothing left to do with it, so it leaves the list unless
 * the picker asks to see it. A short pick stays, since it may still take
 * trees from another lot.
 */
const pickedInFull = (it: PickItem) => it.pick?.status === 'picked';

function readShowPicked(): boolean {
  try { return localStorage.getItem(SHOW_PICKED_KEY) === '1'; } catch { return false; }
}

interface Section {
  key: string;
  title: string;
  items: PickItem[];
}

/** Consecutive runs of a walk-ordered list that stand in the same row. */
function sectionsFor(items: PickItem[]): Section[] {
  const out: Section[] = [];
  for (const it of items) {
    let key: string;
    let title: string;
    if (!it.at_despatch_farm) {
      key = 'elsewhere';
      title = 'Not at this farm yet';
    } else {
      key = `loc:${it.block_name ?? ''}:${it.row_number ?? it.location_name ?? ''}:${it.row_side ?? ''}`;
      title = [it.block_name, it.row_number != null ? rowLabel(it.row_number, it.row_side) : null].filter(Boolean).join(' · ') || it.location_name || 'No location recorded';
    }
    const last = out[out.length - 1];
    if (last && last.key === key) last.items.push(it);
    else out.push({ key, title, items: [it] });
  }
  return out;
}

function ProgressBar({ done, of }: { done: number; of: number }) {
  const pct = of ? Math.round((done / of) * 100) : 0;
  return (
    <div className="h-2.5 rounded-full bg-neutral-100 overflow-hidden">
      <div className="h-full bg-green-500 transition-all" style={{ width: `${pct}%` }} />
    </div>
  );
}

function customerLine(c: PickCustomer, tz: string): string {
  return [
    c.code ? `Store ${c.code}` : null,
    c.order_numbers.join(', '),
    c.collection_at ? `collecting ${formatDateTimeInTz(c.collection_at, tz)}` : null,
  ].filter(Boolean).join(' · ');
}

function PickListPage() {
  const { id } = useParams<{ id: string }>();
  const loadId = Number(id);
  const tz = useOrgTimezone();
  const [params, setParams] = useSearchParams();
  const navigate = useNavigate();
  const customerParam = params.get('customer');
  const [list, setList] = useState<PickList | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [scanning, setScanning] = useState(false);
  const [found, setFound] = useState<number | null>(null);
  const [scanNote, setScanNote] = useState<string | null>(null);
  // Someone else holds the customer this page is open on.
  const [blocked, setBlocked] = useState<{ key: string; message: string } | null>(null);
  const [takingOver, setTakingOver] = useState(false);
  const [showPicked, setShowPickedState] = useState(readShowPicked);
  const setShowPicked = (v: boolean) => {
    setShowPickedState(v);
    try { localStorage.setItem(SHOW_PICKED_KEY, v ? '1' : '0'); } catch { /* per-browser convenience only */ }
  };

  const fetchList = useCallback(async () => {
    try {
      const res = await nurseryService.getPickList(loadId);
      if (res.success) setList(res.data);
      else setError(errorMessage(res, 'Could not load the pick list'));
    } catch (e) {
      setError(errorMessage(e, 'Could not load the pick list'));
    }
  }, [loadId]);

  useEffect(() => { fetchList(); }, [fetchList]);

  const customers = useMemo(
    () => (list ? pickCustomers(list.items, list.load.load_type === 'collection', list.claims ?? []) : []),
    [list],
  );
  const current = customers.length === 1
    ? customers[0]
    : customers.find((c) => c.key === customerParam) ?? null;
  const currentKey = current?.key ?? null;
  const pickedCount = current ? current.items.filter(pickedInFull).length : 0;
  const sections = useMemo(
    () => (current ? sectionsFor(showPicked ? current.items : current.items.filter((i) => !pickedInFull(i))) : []),
    [current, showPicked],
  );
  const isBlocked = !!blocked && blocked.key === currentKey;

  /**
   * Hold the customer this page is open on: claim on opening, renew every
   * couple of minutes, let go on leaving. Refused because someone else holds
   * them, the page says who and offers Take over; renewal keeps trying, so a
   * hold that lapses frees the page by itself. Any other failure (an older
   * server with no claims, no signal) is let pass: picks are checked anyway.
   */
  useEffect(() => {
    if (!currentKey) return;
    let stopped = false;
    const claim = async () => {
      try {
        const res = await nurseryService.claimPickCustomer(loadId, currentKey);
        if (stopped) return;
        if (res.success && res.data) setList(res.data);
        setBlocked((b) => (b?.key === currentKey ? null : b));
      } catch (e) {
        if (!stopped && e instanceof ApiError && e.code === 'CUSTOMER_CLAIMED') {
          setBlocked({ key: currentKey, message: e.message });
        }
      }
    };
    claim();
    const timer = setInterval(claim, CLAIM_HEARTBEAT_MS);
    return () => {
      stopped = true;
      clearInterval(timer);
      // Only deletes a hold that is ours, so leaving a customer someone else holds is harmless.
      nurseryService.releasePickCustomer(loadId, currentKey).catch(() => undefined);
    };
  }, [loadId, currentKey]);

  // On the customer list, keep who-is-picking-whom current.
  useEffect(() => {
    if (currentKey) return;
    const timer = setInterval(fetchList, CHOOSER_REFRESH_MS);
    return () => clearInterval(timer);
  }, [currentKey, fetchList]);

  const takeOver = async () => {
    if (!currentKey) return;
    setTakingOver(true);
    try {
      const res = await nurseryService.claimPickCustomer(loadId, currentKey, true);
      if (res.success && res.data) setList(res.data);
      setBlocked(null);
    } catch (e) {
      setBlocked({ key: currentKey, message: errorMessage(e, 'Could not take this customer over') });
    } finally {
      setTakingOver(false);
    }
  };

  const chooseCustomer = (key: string | null) => {
    setFound(null);
    setScanNote(null);
    setParams(key ? { customer: key } : {});
    window.scrollTo({ top: 0 });
  };

  /**
   * A scanned lot label finds that lot on this customer's list: the first
   * item still to pick from it is highlighted and scrolled to. A lot that is
   * on the load for someone else says who, rather than leaving the picker
   * wondering whether the scan worked.
   */
  const onScan = (raw: string) => {
    setScanning(false);
    const scan = parseLotScan(raw);
    if (!scan || !current) return;
    const lotName = 'lotId' in scan ? `Lot #${scan.lotId}` : scan.code;
    const isLot = (i: PickItem) => scanMatches(scan, { id: i.lot_id, code: i.lot_code });
    const matches = current.items.filter(isLot);
    if (!matches.length) {
      setFound(null);
      const others = customers.filter((c) => c !== current && c.items.some(isLot)).map((c) => c.name);
      setScanNote(others.length
        ? `${lotName} is for ${others.join(', ')}, not ${current.name}.`
        : `${lotName} is not on this load.`);
      return;
    }
    const target = matches.find((i) => !i.pick) ?? matches[0];
    // A scan of a lot already picked in full has to show its card.
    if (pickedInFull(target)) setShowPicked(true);
    setFound(target.lot_id);
    setScanNote(`${matches.length} item${matches.length === 1 ? '' : 's'} for ${current.name} ${matches.length === 1 ? 'is' : 'are'} from this lot.`);
    requestAnimationFrame(() => document.getElementById(`pick-${target.allocation_id}`)?.scrollIntoView({ behavior: 'smooth', block: 'center' }));
  };

  if (!list) {
    return (
      <div className="max-w-2xl mx-auto px-4 py-6">
        {error ? (
          <div className="text-center py-8">
            <p className="text-sm text-red-700 mb-4">{error}</p>
            <Link to="/nursery/picking" className="text-sm text-primary-700 underline">Back to picking</Link>
          </div>
        ) : <div className="h-40 bg-neutral-100 rounded-xl animate-pulse" />}
      </div>
    );
  }

  const { load, progress } = list;
  const editable = ['released', 'picking', 'loaded'].includes(load.status);
  const loadDone = progress.items > 0 && progress.resolved >= progress.items;
  const several = customers.length > 1;
  const nextCustomer = current ? customers.find((c) => c !== current && !customerDone(c) && !heldByOther(c)) : undefined;
  const loadNotes = (load.vehicle || load.driver_name || load.notes) ? (
    <p className="text-xs text-neutral-600 mt-2 break-words whitespace-pre-line">
      {[load.vehicle, load.driver_name].filter(Boolean).join(' · ')}
      {load.notes ? `${load.vehicle || load.driver_name ? '\n' : ''}${load.notes}` : ''}
    </p>
  ) : null;

  return (
    <>
      <PageHeader
        title={LOAD_TYPE_LABELS[load.load_type]}
        subtitle={`${load.load_number} · ${formatLocalDateInTz(load.despatch_date, tz)} · ${load.despatch_farm_name}`}
        icon={<PackageCheck className="h-6 w-6 text-white" />}
        // Back goes up one level, not back in history: a customer opened from
        // a link or a notification still returns to this load's customers.
        onBack={() => (current && several ? chooseCustomer(null) : navigate('/nursery/picking'))}
      />
      <div className="max-w-2xl mx-auto px-4 py-4 space-y-4">
        {error && <div className="bg-red-50 border border-red-200 rounded-lg px-4 py-3 text-sm text-red-700">{error}</div>}

        {!current ? (
          <>
            <div className="bg-white border border-neutral-200 rounded-xl p-4">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <span className="text-sm font-medium text-neutral-800">
                  {progress.resolved} of {progress.items} items · {progress.picked_trees.toLocaleString()} of {progress.trees.toLocaleString()} trees
                </span>
                <LoadStatusBadge status={load.status} />
              </div>
              <div className="mt-2"><ProgressBar done={progress.resolved} of={progress.items} /></div>
              {loadNotes}
            </div>

            {loadDone && (
              <div className="flex items-start gap-2 bg-green-50 border border-green-200 rounded-xl p-4 text-sm text-green-800">
                <CheckCircle2 className="h-5 w-5 flex-shrink-0" />
                <span>Everything on this load has been picked or accounted for.</span>
              </div>
            )}

            {customers.length === 0 ? (
              <p className="text-sm text-neutral-500 text-center py-8">Nothing on this load has stock allocated to it yet.</p>
            ) : (
              <section aria-label="Customers on this load">
                <h2 className="text-sm font-semibold text-neutral-700">Choose a customer to pick for</h2>
                <ul className="mt-2 space-y-3">
                  {customers.map((c) => {
                    const done = customerDone(c);
                    return (
                      <li key={c.key}>
                        <button
                          type="button"
                          onClick={() => chooseCustomer(c.key)}
                          className={`w-full text-left border rounded-xl p-4 flex items-center gap-3 hover:border-primary-400 ${done ? 'bg-white border-green-200' : heldByOther(c) ? 'bg-neutral-50 border-neutral-200' : 'bg-white border-neutral-200'}`}
                        >
                          <div className="min-w-0 flex-1">
                            <div className="flex items-start gap-2">
                              {done && <CheckCircle2 className="h-5 w-5 text-green-600 flex-shrink-0" aria-label="All picked" />}
                              <span className="font-medium text-neutral-900 break-words min-w-0">{c.name}</span>
                            </div>
                            <p className="text-xs text-neutral-500 break-words mt-0.5">{customerLine(c, tz)}</p>
                            {c.claim && !done && (
                              <p className={`inline-flex items-center gap-1 mt-1.5 text-xs font-medium rounded-full px-2 py-0.5 max-w-full ${c.claim.mine ? 'bg-primary-50 text-primary-800' : 'bg-amber-50 text-amber-800'}`}>
                                <UserRound className="h-3.5 w-3.5 flex-shrink-0" />
                                <span className="break-words min-w-0">{c.claim.mine ? 'You are picking' : `${c.claim.user_name || 'Someone else'} is picking`}</span>
                              </p>
                            )}
                            <p className="text-xs text-neutral-700 mt-2">
                              {c.resolved} of {c.items.length} items · {c.picked_trees.toLocaleString()} of {c.trees.toLocaleString()} trees
                            </p>
                            <div className="mt-1"><ProgressBar done={c.resolved} of={c.items.length} /></div>
                          </div>
                          <ChevronRight className="h-5 w-5 text-neutral-400 flex-shrink-0" />
                        </button>
                      </li>
                    );
                  })}
                </ul>
              </section>
            )}
          </>
        ) : (
          <>
            <div className="bg-white border border-neutral-200 rounded-xl p-4">
              <div className="flex flex-wrap items-start justify-between gap-2">
                <div className="min-w-0">
                  <p className="text-sm font-semibold text-neutral-900 break-words">{current.name}</p>
                  <p className="text-xs text-neutral-500 break-words">{customerLine(current, tz)}</p>
                </div>
                <LoadStatusBadge status={load.status} />
              </div>
              <p className="text-sm font-medium text-neutral-800 mt-2">
                {current.resolved} of {current.items.length} items · {current.picked_trees.toLocaleString()} of {current.trees.toLocaleString()} trees
              </p>
              <div className="mt-2"><ProgressBar done={current.resolved} of={current.items.length} /></div>
              {loadNotes}
            </div>

            {isBlocked ? (
              <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 text-sm text-amber-900 space-y-3">
                <div className="flex items-start gap-2">
                  <Hand className="h-5 w-5 flex-shrink-0" />
                  <span className="break-words min-w-0">{blocked?.message}</span>
                </div>
                <p className="text-xs break-words">
                  Take over only if they have stopped. Their picks so far stay recorded, and they cannot pick for {current.name} once you have taken over.
                </p>
                <div className="flex flex-wrap gap-2">
                  <Button size="sm" onClick={takeOver} disabled={takingOver} className="max-w-full">
                    <span className="truncate">{takingOver ? 'Taking over…' : 'Take over'}</span>
                  </Button>
                  {several && (
                    <Button variant="outline" size="sm" onClick={() => chooseCustomer(null)}>Choose another customer</Button>
                  )}
                </div>
              </div>
            ) : (<>
            <div className="flex flex-wrap items-center gap-2">
              <Button variant="outline" size="sm" onClick={() => { setScanNote(null); setScanning(true); }}>
                <ScanLine className="h-4 w-4" /> Scan a lot label
              </Button>
              {pickedCount > 0 && (
                <Button variant="outline" size="sm" onClick={() => setShowPicked(!showPicked)} aria-pressed={showPicked}>
                  {showPicked
                    ? <><EyeOff className="h-4 w-4" /> Hide picked</>
                    : <><Eye className="h-4 w-4" /> Show picked ({pickedCount})</>}
                </Button>
              )}
              {scanNote && <span className="text-sm text-neutral-700 break-words min-w-0">{scanNote}</span>}
            </div>

            {customerDone(current) && (
              <div className="bg-green-50 border border-green-200 rounded-xl p-4 text-sm text-green-800 space-y-3">
                <div className="flex items-start gap-2">
                  <CheckCircle2 className="h-5 w-5 flex-shrink-0" />
                  <span className="break-words min-w-0">
                    {several
                      ? `Everything for ${current.name} has been picked or accounted for.`
                      : 'Everything on this load has been picked or accounted for.'}
                  </span>
                </div>
                {nextCustomer && (
                  <Button size="sm" onClick={() => chooseCustomer(nextCustomer.key)} className="max-w-full">
                    <span className="truncate">Next: {nextCustomer.name}</span>
                    <ArrowRight className="h-4 w-4 flex-shrink-0" />
                  </Button>
                )}
                {several && !nextCustomer && loadDone && (
                  <p>That was the last customer: everything on this load is picked or accounted for.</p>
                )}
              </div>
            )}

            {sections.map((s) => (
              <section key={`${s.key}:${s.items[0].allocation_id}`} aria-label={s.title}>
                <h2 className="text-sm font-semibold text-neutral-700 break-words">{s.title}</h2>
                <ul className="mt-2 space-y-3">
                  {s.items.map((it) => (
                    <PickItemCard
                      key={it.allocation_id}
                      item={it}
                      loadId={load.id}
                      despatchFarmName={load.despatch_farm_name}
                      timezone={tz}
                      editable={editable}
                      onChanged={setList}
                      highlighted={found === it.lot_id}
                    />
                  ))}
                </ul>
              </section>
            ))}
            </>)}
          </>
        )}
      </div>
      {scanning && <BarcodeScanner label="Scan a lot label" onScan={onScan} onClose={() => setScanning(false)} />}
    </>
  );
}

export default PickListPage;
